import { Link } from 'react-router-dom'
import { Head } from 'vite-react-ssg'
import { getCanonicalUrl } from '../utils/canonical'
import {
  ThumbsDown,
  ThumbsUp,
  Image,
  MousePointer,
  Eye,
  FormInput,
  MessageSquare,
  Zap,
  AlertTriangle,
  CheckCircle,
  XCircle,
} from 'lucide-react'

export default function AccessibilityGuidePage() {
  const examples = [
    {
      id: 1,
      title: 'Image Descriptions',
      bad: {
        subtitle: 'Missing Image Descriptions',
        explanation: 'Screen readers, which read websites aloud for visually impaired users, cannot see images. Without a text description (an "alt tag"), the user gets no information.',
        demo: (
          <div className="bg-gray-100 rounded-lg p-4 text-center">
            <div className="w-full h-32 bg-gray-300 rounded flex items-center justify-center text-gray-500">
              <Image className="w-12 h-12" />
            </div>
            <p className="text-xs text-gray-400 mt-2">Image with empty alt=""</p>
          </div>
        ),
      },
      good: {
        subtitle: 'Descriptive Image Text',
        explanation: 'This image has descriptive alt text: "A small corgi puppy lying on a wooden floor." A screen reader will announce this, conveying the image\'s meaning.',
        demo: (
          <div className="bg-gray-100 rounded-lg p-4 text-center">
            <div className="w-full h-32 bg-gradient-to-br from-amber-200 to-orange-300 rounded flex items-center justify-center">
              <span className="text-4xl">🐕</span>
            </div>
            <p className="text-xs text-green-600 mt-2 font-medium">alt="A small corgi puppy lying on a wooden floor"</p>
          </div>
        ),
      },
      icon: Image,
    },
    {
      id: 2,
      title: 'Interactive Controls',
      bad: {
        subtitle: 'Non-Standard Controls',
        explanation: 'This looks like a button, but it\'s just a styled <div>. Keyboard users can\'t "Tab" to it, and screen readers won\'t announce it as a button. It\'s invisible to assistive technology.',
        demo: (
          <div
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded border border-gray-400 text-center cursor-pointer select-none"
            onClick={() => alert('You clicked the fake button!')}
          >
            Click Me (Maybe?)
          </div>
        ),
      },
      good: {
        subtitle: 'Standard HTML Controls',
        explanation: 'This is a proper <button>. It\'s automatically focusable with a keyboard (try the Tab key!), and screen readers announce "Submit, button," making its purpose clear.',
        demo: (
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            onClick={() => alert('You clicked the real button!')}
          >
            Submit
          </button>
        ),
      },
      icon: MousePointer,
    },
    {
      id: 3,
      title: 'Text Contrast',
      bad: {
        subtitle: 'Low Contrast Text',
        explanation: 'When text and background colors are too similar, it\'s hard to read for people with low vision or color blindness. It\'s also difficult for anyone in bright sunlight.',
        demo: (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <p className="text-gray-400">
              Reading this is like trying to spot a polar bear in a snowstorm. Very difficult!
            </p>
          </div>
        ),
      },
      good: {
        subtitle: 'High Contrast Text',
        explanation: 'This dark text on a light background meets contrast standards. It\'s clear and legible for the widest possible audience in various lighting conditions.',
        demo: (
          <div className="bg-white border border-gray-300 rounded-lg p-4">
            <p className="text-gray-900 font-medium">
              This text is bold and clear, making it easy for everyone to read.
            </p>
          </div>
        ),
      },
      icon: Eye,
    },
    {
      id: 4,
      title: 'Form Labels',
      bad: {
        subtitle: 'Unlabeled Form Fields',
        explanation: 'Without a proper <label>, a screen reader doesn\'t know what this input field is for. It might just announce "edit text," leaving the user to guess.',
        demo: (
          <div>
            <input
              type="email"
              placeholder="Your Email..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-300"
            />
          </div>
        ),
      },
      good: {
        subtitle: 'Properly Labeled Form Fields',
        explanation: 'Here, the <label> is programmatically linked to the input field. A screen reader will now correctly announce "Your Email Address:" when the user focuses on the field.',
        demo: (
          <div>
            <label htmlFor="email_good" className="block font-medium text-gray-700 mb-1">
              Your Email Address:
            </label>
            <input
              id="email_good"
              type="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            />
          </div>
        ),
      },
      icon: FormInput,
    },
    {
      id: 5,
      title: 'ARIA & Context',
      bad: {
        subtitle: 'Missing Context for Pop-ups',
        explanation: 'When a dialog appears without ARIA attributes, a screen reader user has no idea what happened. Their focus can still interact with the page behind it, causing total confusion.',
        demo: (
          <div
            className="bg-gray-300 text-gray-700 px-4 py-2 rounded border border-gray-400 text-center cursor-pointer select-none"
            onClick={() => alert('This is just a simple alert. A real inaccessible modal would be even more confusing!')}
          >
            Show Info
          </div>
        ),
      },
      good: {
        subtitle: 'Clear Context for Pop-ups (ARIA)',
        explanation: 'Using ARIA attributes, we can tell screen readers a dialog has opened, what it\'s for, and "trap" the keyboard focus inside until it\'s closed. This provides essential context.',
        demo: (
          <button
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            onClick={() => alert('A real accessible modal would use JavaScript to manage focus and announce its role via ARIA.')}
            aria-haspopup="dialog"
          >
            Show Info
          </button>
        ),
      },
      icon: MessageSquare,
    },
    {
      id: 6,
      title: 'Animation Safety',
      bad: {
        subtitle: 'Flashing or Blinking Content',
        explanation: 'Rapidly flashing content is not just distracting; it can trigger seizures in people with photosensitive epilepsy. It must be avoided.',
        demo: (
          <div className="text-center">
            <span className="text-xl font-bold text-red-600 animate-pulse">
              ⚡ FLASH SALE! ⚡
            </span>
          </div>
        ),
      },
      good: {
        subtitle: 'Stable and Clear Content',
        explanation: 'Important information should be presented clearly without flashing. This design is safe, professional, and ensures the message is received without causing harm.',
        demo: (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
            <span className="font-bold text-red-700">
              Important Announcement
            </span>
          </div>
        ),
      },
      icon: Zap,
    },
  ]

  return (
    <>
      <Head>
        <title>Web Accessibility Explained Simply | achecker.ca</title>
        <meta name="description" content="Learn web accessibility basics with clear examples. See the difference between accessible and inaccessible websites with interactive demonstrations." />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={getCanonicalUrl('/accessibility-guide')} />
      </Head>

      <div className="space-y-12">
        {/* Hero Section */}
        <section className="text-center py-12 bg-gradient-to-br from-blue-50 to-indigo-100 rounded-2xl">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            Making the Web Work for Everyone
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Web accessibility means designing websites so that people with disabilities can use them.
            Think of it like a <strong>ramp next to a staircase</strong>: the ramp allows someone in a wheelchair
            to enter the building. On the web, we build digital ramps so everyone can access information,
            regardless of visual, hearing, motor, or cognitive impairments.
          </p>
        </section>

        {/* Examples Grid */}
        {examples.map((example) => {
          const Icon = example.icon
          return (
            <section key={example.id} className="space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">
                  {example.id}. {example.title}
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                {/* Bad Example */}
                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <ThumbsDown className="w-5 h-5 text-red-600" />
                    <h3 className="text-lg font-semibold text-red-800">
                      Inaccessible: {example.bad.subtitle}
                    </h3>
                  </div>
                  <p className="text-sm text-red-700 mb-4">
                    {example.bad.explanation}
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-red-100">
                    {example.bad.demo}
                  </div>
                  <div className="mt-3 flex items-center gap-1 text-xs text-red-600">
                    <XCircle className="w-4 h-4" />
                    <span>Screen readers struggle with this</span>
                  </div>
                </div>

                {/* Good Example */}
                <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <ThumbsUp className="w-5 h-5 text-green-600" />
                    <h3 className="text-lg font-semibold text-green-800">
                      Accessible: {example.good.subtitle}
                    </h3>
                  </div>
                  <p className="text-sm text-green-700 mb-4">
                    {example.good.explanation}
                  </p>
                  <div className="bg-white rounded-lg p-4 border border-green-100">
                    {example.good.demo}
                  </div>
                  <div className="mt-3 flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span>Works for everyone</span>
                  </div>
                </div>
              </div>
            </section>
          )
        })}

        {/* Conclusion */}
        <section className="text-center py-12 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl text-white">
          <h2 className="text-3xl font-bold mb-4">
            Small Changes, Huge Impact
          </h2>
          <p className="text-xl mb-4 opacity-90 max-w-3xl mx-auto">
            Accessibility isn't about adding complex features; it's about making thoughtful choices.
            Using the right HTML elements and considering all types of users leads to a better experience for everyone.
          </p>
          <p className="text-lg mb-8 opacity-80 max-w-2xl mx-auto">
            These small changes create a more inclusive and effective web.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/"
              className="inline-flex items-center justify-center px-8 py-3 bg-white text-blue-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors"
            >
              <Zap className="w-5 h-5 mr-2" />
              Check Your Website Now
            </Link>
            <Link
              to="/wcag-validator"
              className="inline-flex items-center justify-center px-8 py-3 bg-blue-700 text-white rounded-lg font-semibold hover:bg-blue-800 transition-colors border border-blue-500"
            >
              <AlertTriangle className="w-5 h-5 mr-2" />
              WCAG Validator
            </Link>
          </div>
        </section>

        {/* Additional Info */}
        <section className="bg-gray-50 rounded-lg p-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Why This Matters</h2>
          <div className="grid md:grid-cols-3 gap-6 text-sm text-gray-600">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">1 Billion+ People</h3>
              <p>Over 1 billion people worldwide live with some form of disability. That's 15% of the global population who may struggle with inaccessible websites.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Legal Requirements</h3>
              <p>Many countries have laws requiring web accessibility, including AODA in Canada, ADA in the US, and EN 301 549 in the EU.</p>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Better for Everyone</h3>
              <p>Accessible websites are easier to use for everyone—including people using mobile devices, slow connections, or temporary impairments.</p>
            </div>
          </div>
        </section>
      </div>
    </>
  )
}
